import { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useIsAdmin } from '@/lib/queries';
import { date, gradeLabel } from '@/lib/format';
import { usePhotoUrl } from '@/components/Photo';
import { Emblem } from '@/components/Brand';

interface Sheet {
  grading_id: string; for_rank: number; from_rank: number | null; graded_on: string;
  recommended: boolean | null; recommended_by: string | null; recommended_at: string | null; recommendation_note: string | null;
  previously_failed: boolean | null; sessions_per_week: number | null; fee_paid: boolean;
  first_name: string; last_name: string; name_katakana: string | null; email: string | null; phone: string | null; date_of_birth: string | null;
  gender: string | null; nationality: string | null; photo_path: string | null; member_number: number | null;
  started_goju_ryu_on: string | null; started_karate_on: string | null; address: string; next_of_kin: string | null;
  dojo: string | null; dojo_address: string | null; sensei: string | null; membership_expires: string | null;
  passed_9th_kyu_on: string | null; last_graded_on: string | null;
  dan_history: { rank: number; on: string }[]; recent_events: { title: string; on: string }[]; gasshuku_since_last: { title: string; on: string; venue: string | null }[];
}

const years = (from: string | null, on: string) => {
  if (!from) return null;
  const a = new Date(from), b = new Date(on);
  return Math.max(0, b.getFullYear() - a.getFullYear() - (b < new Date(b.getFullYear(), a.getMonth(), a.getDate()) ? 1 : 0));
};
const age = (dob: string | null, on: string) => years(dob, on);

/**
 * Every candidate's grading sheet for one event, one A4 page each, in the
 * order of the Kyu and Dan registration forms. Filled from the portal; the
 * examiner's section is left blank to complete by hand on the day.
 */
export function GradingSheets() {
  const { id = '' } = useParams();
  const admin = useIsAdmin();
  const event = useQuery({
    queryKey: ['sheet-event', id],
    queryFn: async () => { const { data, error } = await supabase.from('event').select('title,starts_at,venue').eq('id', id).single(); if (error) throw error; return data as { title: string; starts_at: string; venue: string | null }; },
  });
  const sheets = useQuery({
    queryKey: ['grading-sheets', id],
    enabled: !!admin.data,
    queryFn: async () => { const { data, error } = await supabase.rpc('grading_sheets', { p_event: id }); if (error) throw error; return data as Sheet[]; },
  });
  useEffect(() => { document.title = event.data ? `Grading sheets: ${event.data.title}` : 'Grading sheets'; }, [event.data]);

  if (admin.isLoading) return null;
  if (!admin.data) return <Navigate to="/home" replace />;
  const list = sheets.data ?? [];

  return (
    <div className="min-h-screen bg-stone-100 print:bg-white">
      <style>{`@page { size: A4; margin: 12mm; } @media print { .no-print { display: none !important; } .sheet { box-shadow: none !important; margin: 0 !important; border: 0 !important; page-break-after: always; break-after: page; } .sheet:last-child { page-break-after: auto; break-after: auto; } }`}</style>
      <div className="no-print sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 bg-white px-6 py-3">
        <div>
          <Link to={`/admin/events/${id}`} className="text-sm text-ink-500 hover:underline">← Back to the event</Link>
          <p className="font-display text-lg font-bold">{event.data?.title}: {list.length} grading {list.length === 1 ? 'sheet' : 'sheets'}</p>
        </div>
        <button className="btn-primary" onClick={() => window.print()} disabled={!list.length}><Printer size={16} /> Print all</button>
      </div>
      {sheets.isLoading && <p className="p-8 text-center text-ink-500">Loading…</p>}
      {!sheets.isLoading && !list.length && <p className="p-8 text-center text-ink-500">Nobody has entered this grading yet.</p>}
      <div className="py-6 print:py-0">
        {list.map(s => <SheetPage key={s.grading_id} s={s} event={event.data} />)}
      </div>
    </div>
  );
}

function SheetPage({ s, event }: { s: Sheet; event?: { title: string; starts_at: string; venue: string | null } }) {
  const photo = usePhotoUrl(s.photo_path);
  const dan = s.for_rank >= 11;
  const on = s.graded_on;
  const F = ({ label, value, wide }: { label: string; value: React.ReactNode; wide?: boolean }) => (
    <div className={wide ? 'col-span-2' : ''}><p className="text-[10px] font-bold uppercase tracking-wider text-ink-500">{label}</p><p className="min-h-[18px] border-b border-ink-400 pb-0.5 text-[12px]">{value || ' '}</p></div>
  );
  const Box = ({ label }: { label: string }) => <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 border border-ink-900" />{label}</span>;

  return (
    <section className="sheet mx-auto mb-6 w-[210mm] min-h-[297mm] bg-white p-[12mm] text-ink-900 shadow-sm">
      <header className="flex items-start justify-between gap-4 border-b-2 border-ink-900 pb-3">
        <div className="flex items-center gap-3">
          <Emblem size={44} />
          <div>
            <p className="font-display text-xl font-bold leading-tight">{dan ? 'Request for IOGKF Dan registration' : 'Kyu grade registration'}</p>
            <p className="text-[12px] text-ink-700">{event?.title}{event?.venue && `, ${event.venue}`} · {date(on)}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Grading for</p>
          <p className="font-display text-2xl font-extrabold text-brand-600">{gradeLabel(s.for_rank)}</p>
          <p className="text-[11px] text-ink-500">from {gradeLabel(s.from_rank)}</p>
        </div>
      </header>

      <div className="mt-4 grid grid-cols-[1fr_35mm] gap-5">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-5 gap-y-3">
            <F label="First name" value={s.first_name} />
            <F label="Family name" value={s.last_name} />
            {dan && <F label="Name in katakana" value={s.name_katakana} wide />}
            <F label="Date of birth (age)" value={s.date_of_birth && `${date(s.date_of_birth)} (${age(s.date_of_birth, on)})`} />
            {dan ? <F label="Gender / nationality" value={[s.gender && s.gender[0]!.toUpperCase() + s.gender.slice(1), s.nationality].filter(Boolean).join(' / ')} /> : <F label="Next of kin (under 18)" value={s.next_of_kin} />}
            <F label="Email" value={s.email} />
            <F label="Telephone" value={s.phone} />
            <F label="Home address" value={s.address} wide />
            {!dan && <F label="EGKA membership no." value={s.member_number} />}
            {!dan && <F label="Membership expires" value={s.membership_expires && date(s.membership_expires)} />}
          </div>
        </div>
        <div className="flex h-[45mm] w-[35mm] items-center justify-center border border-ink-900 bg-stone-50">
          {photo ? <img src={photo} alt="" className="h-full w-full object-cover" /> : <span className="px-2 text-center text-[10px] font-semibold text-brand-700">No photo on record. Cannot grade without one.</span>}
        </div>
      </div>

      <h3 className="mt-6 border-b border-ink-900 pb-1 font-display text-sm font-bold uppercase tracking-wider">Training record</h3>
      <div className="mt-3 grid grid-cols-4 gap-x-5 gap-y-3">
        <F label="Present rank" value={gradeLabel(s.from_rank)} />
        <F label="Years in Goju-Ryu" value={years(s.started_goju_ryu_on, on)} />
        <F label="Total years in karate" value={years(s.started_karate_on ?? s.started_goju_ryu_on, on)} />
        <F label="Sessions per week" value={s.sessions_per_week} />
        <F label="Dojo" value={s.dojo} wide />
        <F label="Sensei" value={s.sensei} wide />
        {!dan && <F label="Passed 9th kyu" value={s.passed_9th_kyu_on && date(s.passed_9th_kyu_on)} wide />}
        {!dan && <F label="Last grading" value={s.last_graded_on && date(s.last_graded_on)} wide />}
      </div>

      {dan ? (
        <>
          <p className="mt-4 text-[10px] font-bold uppercase tracking-wider text-ink-500">IOGKF grading history</p>
          <div className="mt-1 grid grid-cols-6 border border-ink-900 text-center text-[11px]">
            {['Shodan', 'Nidan', 'Sandan', 'Yondan', 'Godan', 'Rokudan'].map((n, i) => {
              const h = s.dan_history.find(d => d.rank === 11 + i);
              return <div key={n} className="border-r border-ink-900 last:border-r-0"><p className="border-b border-ink-900 bg-stone-50 py-0.5 font-semibold">{n}</p><p className="py-1">{h ? date(h.on) : ' '}</p></div>;
            })}
          </div>
          <p className="mt-3 text-[10px] font-bold uppercase tracking-wider text-ink-500">Gasshuku since last grading (up to 5)</p>
          <table className="mt-1 w-full border border-ink-900 text-[11px]"><tbody>
            {Array.from({ length: 5 }, (_, i) => s.gasshuku_since_last[i]).map((g, i) => <tr key={i} className="border-b border-ink-400 last:border-0"><td className="w-28 border-r border-ink-400 px-2 py-1">{g ? date(g.on) : ' '}</td><td className="px-2 py-1">{g ? `${g.title}${g.venue ? `, ${g.venue}` : ''}` : ''}</td></tr>)}
          </tbody></table>
        </>
      ) : (
        <>
          <p className="mt-4 text-[10px] font-bold uppercase tracking-wider text-ink-500">EGKA or IOGKF events in the last 12 months</p>
          <table className="mt-1 w-full border border-ink-900 text-[11px]"><tbody>
            {[0, 1].map(i => <tr key={i} className="border-b border-ink-400 last:border-0"><td className="px-2 py-1">{s.recent_events[i]?.title ?? ' '}</td><td className="w-32 border-l border-ink-400 px-2 py-1">{s.recent_events[i] ? date(s.recent_events[i]!.on) : ''}</td></tr>)}
          </tbody></table>
        </>
      )}

      <div className="mt-6 grid grid-cols-2 gap-5">
        <div className="border border-ink-900 p-3">
          <p className="font-display text-sm font-bold">Instructor</p>
          <div className="mt-2 space-y-2 text-[12px]">
            <p>Recommended for <strong>{gradeLabel(s.for_rank)}</strong>: {s.recommended === true ? <strong>Yes</strong> : s.recommended === false ? <strong className="text-brand-700">No</strong> : <span className="text-brand-700">not yet given</span>}</p>
            {s.recommended !== null && <p>By {s.recommended_by}{s.recommended_at && `, ${date(s.recommended_at)}`} (in the EGKA portal)</p>}
            {s.recommendation_note && <p className="italic">"{s.recommendation_note}"</p>}
            <p>Previously failed this grading: <strong>{s.previously_failed ? 'Yes' : 'No'}</strong></p>
            {dan && <p>Written exam score (sandan and below): ______ %</p>}
          </div>
        </div>
        <div className="border border-ink-900 p-3">
          <p className="font-display text-sm font-bold">Examiner</p>
          <div className="mt-2 space-y-3 text-[12px]">
            <p>Grading fee paid: {s.fee_paid ? <strong>Yes (recorded)</strong> : <span className="inline-flex gap-3"><Box label="Cash" /><Box label="Card" /><Box label="Other" /></span>}</p>
            {!dan && <p>EGKA membership checked: <strong>{s.membership_expires && s.membership_expires >= on ? `Yes, to ${date(s.membership_expires)}` : 'NOT CURRENT'}</strong></p>}
            <p className="flex gap-4">Result: <Box label="Pass" /><Box label="Fail" /></p>
            {dan && <p>Rank awarded: ______________________</p>}
            <p className="pt-3">Signature: ______________________</p>
          </div>
        </div>
      </div>
      <p className="mt-4 text-[10px] text-ink-500">Printed from the EGKA members portal. Record the result in Admin → Events → {event?.title} → Grading candidates.</p>
    </section>
  );
}
